/**
 * OAuth CSRF-protection helper.
 *
 * The provider auth request carries a `state` value; the callback must receive
 * the same value back. We generate a cryptographically-random state, persist it
 * keyed per provider, and require an exact match on return. State is single-use:
 * validating (or explicitly clearing) removes it.
 *
 * STORAGE SCOPE
 * -------------
 * By default the nonce lives in sessionStorage, which scopes it to the tab that
 * started the flow and clears it when that tab closes. That is the right choice
 * for every provider whose callback returns in the *same* tab (LinkedIn, Meta,
 * GSC, GMB, YouTube, WordPress).
 *
 * GoHighLevel is the exception. Its paid-app install runs in a marketplace
 * modal, and on completion GHL opens the redirect URL in a NEW tab rather than
 * navigating the tab the flow began in. A sessionStorage nonce written before
 * the redirect is therefore invisible to the callback, and validation fails
 * closed on every install. Passing `{ crossTab: true }` moves that provider's
 * nonce to localStorage, which is shared across tabs of the same origin, so the
 * callback tab can still see it.
 *
 * localStorage does not clear on tab close, so a cross-tab nonce also carries a
 * short TTL: an abandoned flow leaves at most one expired entry, which the next
 * create/validate for that provider overwrites or rejects.
 *
 * Both stores are origin-scoped and the nonce stays single-use, so the CSRF
 * guarantee is identical either way.
 */

const STORAGE_PREFIX = "oauth_state:";

/** How long a cross-tab (localStorage) nonce stays valid. A consent round-trip
 *  is seconds-to-a-minute in practice; 10 minutes covers a slow payment step
 *  without letting an abandoned nonce linger. Unused for sessionStorage, which
 *  is bounded by the tab's lifetime. */
const CROSS_TAB_TTL_MS = 10 * 60 * 1000;

export interface OAuthStateOptions {
  /** Persist in localStorage (shared across tabs) instead of sessionStorage.
   *  Required for providers whose OAuth callback can land in a different tab
   *  than the one that started the flow — currently only GoHighLevel. */
  crossTab?: boolean;
}

interface StoredNonce {
  value: string;
  /** Epoch ms after which the nonce is stale. Only set for cross-tab nonces. */
  expiresAt?: number;
}

function storageKey(provider: string): string {
  return `${STORAGE_PREFIX}${provider}`;
}

function storageFor(options?: OAuthStateOptions): Storage {
  return options?.crossTab ? window.localStorage : window.sessionStorage;
}

/** Generate a cryptographically-random, URL-safe state token. */
function randomState(): string {
  const bytes = new Uint8Array(32);
  // crypto is available in all supported browsers; fall back only in non-DOM tests.
  const cryptoObj: Crypto | undefined =
    typeof globalThis !== "undefined" ? (globalThis.crypto as Crypto | undefined) : undefined;
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let hex = "";
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, "0");
  return hex;
}

/**
 * Create and persist a fresh state token for the given provider, then return it
 * so the caller can add it to the authorization URL.
 *
 * Pass `{ crossTab: true }` for providers whose callback can open in a new tab
 * (GoHighLevel). The same option MUST be passed to `validateOAuthState` on the
 * callback, or the stores will not match.
 */
export function createOAuthState(provider: string, options?: OAuthStateOptions): string {
  const state = randomState();
  const entry: StoredNonce = { value: state };
  if (options?.crossTab) {
    entry.expiresAt = Date.now() + CROSS_TAB_TTL_MS;
  }
  try {
    storageFor(options).setItem(storageKey(provider), JSON.stringify(entry));
  } catch {
    // Storage unavailable (private mode / SSR) — the callback will fail closed
    // since it has nothing to match against.
  }
  return state;
}

/** Read and parse the stored nonce, tolerating a legacy bare-string value and a
 *  corrupt/expired entry (all treated as "nothing stored"). */
function readStoredNonce(provider: string, options?: OAuthStateOptions): string | null {
  let raw: string | null = null;
  try {
    raw = storageFor(options).getItem(storageKey(provider));
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: StoredNonce;
  try {
    const candidate = JSON.parse(raw);
    // A pre-envelope nonce (plain hex string) — accept it so an in-flight OAuth
    // round-trip that spans a deploy still validates.
    parsed =
      typeof candidate === "string"
        ? { value: candidate }
        : (candidate as StoredNonce);
  } catch {
    // Not JSON: also a legacy bare string.
    parsed = { value: raw };
  }

  if (!parsed || typeof parsed.value !== "string" || !parsed.value) return null;
  if (typeof parsed.expiresAt === "number" && Date.now() > parsed.expiresAt) return null;
  return parsed.value;
}

/**
 * Validate the state returned on the callback against the stored value, then
 * clear it (single-use). Returns true only when a stored value existed, had not
 * expired, and matches the received value exactly. Missing, expired or
 * mismatched state fails closed.
 *
 * `options` must match what was passed to `createOAuthState` for this provider.
 */
export function validateOAuthState(
  provider: string,
  received: string | null | undefined,
  options?: OAuthStateOptions,
): boolean {
  const stored = readStoredNonce(provider, options);
  try {
    storageFor(options).removeItem(storageKey(provider));
  } catch {
    /* ignore */
  }
  if (!stored || !received) return false;
  return stored === received;
}

/** Drop any stored state for the provider (e.g. when abandoning a flow).
 *  `options` must match what was passed to `createOAuthState`. */
export function clearOAuthState(provider: string, options?: OAuthStateOptions): void {
  try {
    storageFor(options).removeItem(storageKey(provider));
  } catch {
    /* ignore */
  }
}
